import { type FC } from "react";
import { Partner } from "@/types/partner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatCurrency } from "@/lib/utils";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";

interface PartnersListProps {
  partners: Partner[];
  loading?: boolean;
  onRefresh?: () => void;
}

export const PartnersList: FC<PartnersListProps> = ({
  partners,
  loading = false,
  onRefresh,
}) => {
  if (loading) {
    return (
      <div className="space-y-4 p-4">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  return (
    <ScrollArea className="h-[calc(100vh-24rem)]">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="min-w-[200px]">Socio</TableHead>
            <TableHead className="min-w-[150px]">Fecha de Ingreso</TableHead>
            <TableHead className="min-w-[120px]">Referidos</TableHead>
            <TableHead className="min-w-[150px]">Ganancias Totales</TableHead>
            <TableHead className="min-w-[150px]">Pago Pendiente</TableHead>
            <TableHead className="min-w-[100px]">Estado</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {partners.length === 0 ? (
            <TableRow>
              <TableCell 
                colSpan={6} 
                className="h-[200px] text-center text-muted-foreground"
              >
                No hay socios registrados
              </TableCell>
            </TableRow>
          ) : (
            partners.map((partner) => (
              <TableRow key={partner.id}>
                <TableCell>
                  <div className="flex flex-col">
                    <span className="font-medium">{partner.display_name}</span>
                    <span className="text-sm text-muted-foreground">
                      {partner.email}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  {format(new Date(partner.joined_at), "PPP", { locale: es })}
                </TableCell>
                <TableCell>
                  <div className="flex flex-col">
                    <span className="font-medium">{partner.total_referrals}</span>
                    <span className="text-sm text-muted-foreground">
                      {partner.active_referrals} activos
                    </span>
                  </div>
                </TableCell>
                <TableCell>{formatCurrency(partner.total_earnings)}</TableCell>
                <TableCell>{formatCurrency(partner.pending_payment)}</TableCell>
                <TableCell>
                  <Badge
                    variant={partner.is_active ? "success" : "secondary"}
                  >
                    {partner.is_active ? "Activo" : "Inactivo"}
                  </Badge>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </ScrollArea>
  );
};
